// Schemas Zod canônicos compartilhados entre backend e TV/mobile.
// Single source of truth pros tipos de domínio.
import { z } from 'zod';
import { DEVICE_KEY_REJECTED_MALFORMED, DEVICE_KEY_REJECTED_RESERVED, DEVICE_PRIMARY_ID_MAX, isMalformedDeviceKey, isReservedDeviceKey, } from './device-key';
// ===== Tier =====
export const TierSchema = z.enum(['vlow', 'low', 'mid', 'high', 'ultra']);
// ===== Platform =====
// Mesmo source-code roda em várias stacks. Adicionar plataforma nova aqui é
// seguro: o stream mode (resolveStreamMode) faz DENYLIST — só navegador proxia,
// todo o resto vai DIRETO por padrão. Stack nova nunca vaza banda por engano.
export const PlatformSchema = z.enum([
    'tizen', // Samsung TV
    'webos', // LG TV
    'androidtv', // Android TV / Google TV / Bravia
    'firetv', // Amazon Fire TV
    'vidaa', // Hisense (VIDAA OS)
    'desktop', // app desktop nativo (.exe — Electron/Tauri, player nativo)
    'web', // navegador desktop puro
    'mobile-pwa', // navegador mobile / PWA
]);
// Plataformas que rodam em contexto de NAVEGADOR (sujeitas à trava de
// mixed-content/cert/CORS). SÓ estas usam proxy.
const BROWSER_PLATFORMS = new Set(['web', 'mobile-pwa']);
/** Decide se o stream vai direto pro provedor ou passa pelo proxy. Denylist:
 *  só navegador proxia; todo o resto (TV/desktop/futuras) vai direto. */
export function resolveStreamMode(platform) {
    return BROWSER_PLATFORMS.has(platform) ? 'proxy' : 'direct';
}
// ===== Branding =====
export const ProviderBrandingSchema = z.object({
    primaryColor: z.string(),
    secondaryColor: z.string().optional(),
    /**
     * LEGADO — alias de LEITURA da cor primária. Provedores antigos (ex.: HXTV
     * em prod) persistiram a marca como `{ color: '#7C3AED' }`, sem
     * `primaryColor`, e esse objeto já vive no `localStorage`
     * (`iptv:session.branding`) de aparelhos reais, lido cru na reidratação.
     * O canônico é `primaryColor` (schema/seed/admin concordam); este campo é só
     * TOLERÂNCIA — aditivo e opcional, não muda o que o backend produz. Ler
     * SEMPRE via `primaryColor ?? color` (ver resolveBrandColors no app de TV).
     */
    color: z.string().optional(),
    logoUrl: z.string().url().or(z.literal('')),
    name: z.string(),
    locale: z.string().default('pt-BR'),
    /**
     * País do parceiro (ISO 3166-1 alpha-2). Usado pra dados regulatórios:
     * - ageRating do TMDB (release_dates por país: BR usa L/10/12/14/16/18,
     *   US usa G/PG/PG-13/R, etc.)
     * - Posters traduzidos quando TMDB tiver versão regional
     * Default `BR`. Painel parceiro (Fase 4B) expõe controle.
     */
    country: z.string().length(2).default('BR'),
    /**
     * EIXO 1 — TIPO de áudio padrão do catálogo do parceiro. Usado quando o
     * título não tem tag explícita (DUB, LEG, etc.) — assumimos esse default.
     * Consumido SERVER-SIDE no enrichment (catalog-service) como fallback da
     * badge `audioLanguage` (DUB/LEG/NAC/OG no app TV via ContentBadges).
     * NÃO é o controle de seleção de faixa do usuário (esse é o
     * `audioPreference` do settings-store, per-usuário).
     * - dubbed: dublado no idioma do país (típico catálogo BR)
     * - subtitled: legendado, áudio original
     * - national: produção nacional (áudio já no idioma do país)
     * - original: áudio original sem legenda assumida
     *
     * Ortogonal ao EIXO 2 (`audioLangPref`): "Dublado" é um TIPO; em QUAL
     * língua é separado. Na UI do admin os 2 eixos viram 2 controles distintos
     * (Tipo de áudio + Língua preferida).
     */
    defaultAudioLanguage: z
        .enum(['dubbed', 'subtitled', 'national', 'original'])
        .default('dubbed'),
    /**
     * EIXO 2 — LÍNGUA preferida do áudio (ortogonal ao TIPO acima). Quando há
     * múltiplas faixas/variantes, qual língua priorizar. `null`/ausente = sem
     * preferência de língua (deixa o tipo decidir sozinho).
     *
     * ISO-639-2 pra espelhar as tags que o catálogo usa (por/eng/spa).
     * Independente do `defaultAudioLanguage`: dá pra ter "Dublado + Português"
     * ou "Original + sem preferência de língua".
     *
     * COMPAT: antes os códigos por/eng/spa eram oferecidos junto com
     * dubbed/original num Select único (fachada — o schema canônico só aceitava
     * o enum de TIPO, então língua nunca persistia validamente). Agora cada eixo
     * tem seu campo. Leitura de branding legado deriva este campo via
     * `splitLegacyAudioPref` (audio-pref.ts).
     */
    audioLangPref: z.enum(['por', 'eng', 'spa']).nullable().optional(),
    /**
     * Habilita preload agressivo do vídeo (preload="auto") quando user fica
     * no detalhe do filme. Click em "Assistir" → primeiro frame quase
     * instantâneo. CONTRA: gasta banda do parceiro (cada filme aberto em
     * detalhe baixa MBs sem certeza que user vai assistir).
     *
     * Cliente ainda aplica salvaguardas: só ativa em conexão 4G+, sem
     * data-saver, tier !== vlow/low, e após 3s no detalhe (sinal de interesse).
     *
     * Default TRUE no MVP (ganho perceptível alto). Painel parceiro (Fase 4B)
     * vai expor toggle pra opt-out caso parceiro queira economizar banda.
     */
    aggressivePreload: z.boolean().default(true),
});
// ===== Channels & Categories =====
export const ChannelQualitySchema = z.enum(['SD', 'HD', 'FHD', '4K']).nullable();
export const ChannelCodecSchema = z.enum(['H264', 'H265']).nullable();
// ===== Content quality + source (VOD/Series) =====
/** Resolução do conteúdo. Inclui UHD (acima de 4K), ausente em ChannelQuality. */
export const ContentQualitySchema = z.enum(['SD', 'HD', 'FHD', '4K', 'UHD']);
/** Origem da gravação. CAM/TS/HDTS/HDCAM/TC = pirata baixa qualidade (warning UI).
 *  WEBRIP/WEBDL/BLURAY/BRRIP/DVDRIP = qualidade boa. TC = telecine (o backend
 *  também mapeia TELECINE e HDTC pra ele). */
export const ContentSourceSchema = z.enum([
    'CAM', 'TS', 'HDTS', 'HDCAM', 'TC', 'WEBRIP', 'WEBDL', 'BLURAY', 'BRRIP', 'DVDRIP',
]);
// Regra "origem ruim" e rótulo do selo: ver ./content-source.ts.
/** Valor do override de origem gravado pelo admin: uma origem do enum, ou
 *  `NONE` = "este arquivo NÃO tem origem a declarar" (desliga a inferida e a do
 *  nome/categoria; o item sai sem selo de origem). */
export const ContentSourceOverrideSchema = z.union([ContentSourceSchema, z.literal('NONE')]);
/** Body do `PUT /admin/v1/catalog/source/:providerId/vod/:sourceId` (só admin).
 *  `source: null` remove o override (volta a valer nome > categoria > inferida). */
export const SourceOverrideRequestSchema = z.object({
    source: ContentSourceOverrideSchema.nullable(),
}).strict();
/** Estado da origem de UM item no match (GET e PUT da rota acima). Datas em ISO.
 *  `sourceOverrideBy`/`sourceOverrideAt` = quem e quando mexeu no override por
 *  último (definir OU remover: o clear mantém o ator e a data na linha).
 *  `sourceEvidence` = o JSON gravado pelo avaliador da origem inferida (motivo,
 *  países, datas). Fica SÓ aqui, no contrato do admin: o VodItem do app não diz de
 *  onde veio a origem (privilégio mínimo, ver docs/specs/api.md §7.5.10). */
export const SourceStateResponseSchema = z.object({
    providerId: z.string(),
    sourceType: z.literal('vod'),
    sourceId: z.string(),
    sourceOverride: ContentSourceOverrideSchema.nullable(),
    sourceOverrideBy: z.string().nullable(),
    sourceOverrideAt: z.string().nullable(),
    sourceInferred: ContentSourceSchema.nullable(),
    sourceEvidence: z.record(z.unknown()).nullable(),
    sourceEvaluatedAt: z.string().nullable(),
});
/** Idioma de áudio detectado/inferido do conteúdo. */
export const AudioLanguageSchema = z.enum(['dubbed', 'subtitled', 'national', 'original']);
/** Plataforma de origem (streaming) — Netflix/Amazon/Disney/etc. */
export const OriginPlatformSchema = z.enum([
    'NETFLIX', 'AMAZON', 'DISNEY', 'HBOMAX', 'PARAMOUNT', 'APPLETV', 'STAR', 'GLOBOPLAY',
]);
export const LiveCategorySchema = z.object({
    id: z.string(),
    name: z.string(),
    sortOrder: z.number().default(0),
});
export const LiveChannelSchema = z.object({
    id: z.string(),
    name: z.string(),
    rawName: z.string().optional(),
    logo: z.string().nullable(),
    categoryId: z.string(),
    epgChannelId: z.string().optional(),
    quality: ChannelQualitySchema.optional(),
    codec: ChannelCodecSchema.optional(),
    language: z.string().nullable().optional(),
    hasArchive: z.boolean().default(false),
    archiveWindowHours: z.number().optional(),
    isAdult: z.boolean().default(false),
    // Curadoria de FAMÍLIA (override do backend). Quando presente, groupLiveChannels
    // usa em vez da heurística de nome. Ausente = comportamento de hoje (retrocompat).
    // familyKey: identidade da família (canais com mesmo key agrupam).
    // familyName: displayName curado (renomear). familyIsolated: não agrupa com ninguém.
    familyKey: z.string().optional(),
    familyName: z.string().optional(),
    familyIsolated: z.boolean().optional(),
});
// ===== EPG =====
export const EpgProgramSchema = z.object({
    id: z.string(),
    channelId: z.string(),
    title: z.string(),
    description: z.string().optional(),
    startTs: z.number(),
    endTs: z.number(),
    hasArchive: z.boolean().default(false),
});
// ===== VOD =====
/**
 * Membro do elenco com foto. Quando preenchido por TMDB, vem com photo.
 * Mantemos `cast: string[]` separado pra retro-compat com Xtream cru
 * (que vem como string "Ator1, Ator2").
 */
export const CastMemberSchema = z.object({
    name: z.string(),
    /** TMDB profile_path (ex: "/abc123.jpg"). Cliente compõe URL completa. */
    photo: z.string().nullable().optional(),
    character: z.string().optional(),
});
/** Fonte do poster final exposto ao cliente, decidida pelo backend
 *  (resolver centralizado). Cliente é renderer puro — usa apenas pra
 *  telemetria/debug ou UI condicional futura.
 *  Detalhes em docs/plan/poster-resolver-centralizado.md. */
export const PosterSourceSchema = z.enum([
    'tmdb',
    'provider',
    'episode-thumb',
    'none',
]);
export const VodItemSchema = z.object({
    id: z.string(),
    name: z.string(),
    poster: z.string().nullable(),
    backdrop: z.string().nullable().optional(),
    plot: z.string().optional(),
    year: z.number().optional(),
    duration: z.number().optional(),
    genre: z.string().optional(),
    rating: z.number().optional(),
    /** Classificação etária regional (BR: L/10/12/14/16/18; US: G/PG/PG-13/R).
     *  Quando vem do TMDB usa release_dates do `branding.country`.
     *  Quando vem do Xtream cru, é string livre não-normalizada. */
    ageRating: z.string().optional(),
    cast: z.array(z.string()).optional(),
    /** Elenco com fotos (TMDB). Quando presente, cliente prefere sobre `cast`. */
    castDetailed: z.array(CastMemberSchema).optional(),
    director: z.string().optional(),
    categoryId: z.string(),
    addedAt: z.number().optional(), // unix sec — pra ordenar recentes
    /** TMDB ID resolvido (cache hit acelera próximas resoluções). */
    tmdbId: z.number().optional(),
    /** Trailer oficial — YouTube key (cliente compõe URL embed). */
    trailerYoutubeKey: z.string().optional(),
    /** IDs TMDB de recomendações (cliente busca metadados quando renderiza). */
    recommendationsTmdbIds: z.array(z.number()).optional(),
    /** Resolução detectada (do título Xtream ou TMDB). UI mostra como badge. */
    quality: ContentQualitySchema.optional(),
    /** Origem da gravação, granular. Quando pirata (isLowQualitySource, em
     *  content-source.ts) a UI mostra o selo de warning "CAM" no lugar da quality. */
    source: ContentSourceSchema.optional(),
    /** Idioma do áudio. Detectado do título; senão usa branding.defaultAudioLanguage. */
    audioLanguage: AudioLanguageSchema.optional(),
    /** HDR detectado (HDR/HDR10/Dolby Vision no título). */
    hdr: z.boolean().optional(),
    /** Plataforma de origem (Netflix/Amazon/etc) — vem do título OU
     *  TMDB watch/providers. */
    originPlatform: OriginPlatformSchema.optional(),
    /** Variantes do MESMO filme (mesmo canonical) no catálogo do parceiro.
     *  Geralmente DUB e LEG têm sourceIds diferentes. Backend popula só em
     *  getVodInfo (não em listas). UI usa pra botão "Trocar" áudio. */
    siblings: z
        .array(z.object({
        sourceId: z.string(),
        audioLanguage: AudioLanguageSchema.optional(),
        quality: ContentQualitySchema.optional(),
        /** Origem do sibling, com a mesma precedência do item (override do
         *  admin > nome cru > categoria > inferida pelo TMDB), em TODO caminho:
         *  com a listagem em cache e no fallback durável (pcm), que lê a origem
         *  do nome/categoria persistida em `source_listed`. Quem consome: a ficha
         *  de filme, via apps/tv/src/screens/movie-variants.ts
         *  (deriveVariantChoice): a cópia de origem ruim (visibleQualityBadges) é
         *  anunciada "CAM" no botão de qualidade e nos selos, nunca pela
         *  resolução, e a escolha automática só cai nela quando não há cópia boa. */
        source: ContentSourceSchema.optional(),
    }))
        .optional(),
    /** True quando o filme tem AMBAS variantes (dublado e legendado) no
     *  catálogo. Backend popula em listas — UI usa pra mostrar badge
     *  "DUB/LEG" combinado em vez do badge individual de áudio. */
    hasMultiAudio: z.boolean().optional(),
    /** True quando o filme tem 2+ siblings com qualities distintas
     *  conhecidas (HD/FHD/4K). Detectado quando NÃO há multi-audio
     *  (esse tem prioridade). UI mostra opção "Trocar qualidade". */
    hasMultiQuality: z.boolean().optional(),
    /** Idioma original (ISO 639-1, ex: 'pt'). Pra detectar conteúdo
     *  brasileiro mesmo quando productionCountries não tem. Vem do TMDB. */
    originalLanguage: z.string().optional(),
    /** Países de produção (ISO 3166-1 alpha-2). 'BR' = filme nacional. */
    productionCountries: z.array(z.string()).optional(),
    /** Score de popularidade do TMDB (atualizado diário pelo refresh worker).
     *  Usado em rails dinâmicos "Em alta". */
    popularity: z.number().optional(),
    /** Data de lançamento completa (ISO YYYY-MM-DD). Pra rails de
     *  "Lançamentos" rolantes (NOW - 180d). Difere de `year` (só ano). */
    releaseDate: z.string().optional(),
    /** Tokens pré-computados para busca client-side (Opção D do Catálogo
     *  v1, 2026-05-11). Backend concatena name + originalTitle normalizados
     *  durante enrich. Cliente usa em scoreAndRank pra match cross-language
     *  (ex: digitar "Supernatural" em PT-BR → matcha "Sobrenatural"
     *  via originalTitle).
     *
     *  Sempre lowercase + accent-stripped. Ausente em items sem TMDB
     *  enrich — scorer degrada graciosamente pro `name`. */
    searchTokens: z.string().optional(),
    /** Fonte do poster atualmente em `poster`. Backend popula em endpoints
     *  enriched-batch pra cliente saber a procedência (telemetria/UI).
     *  Ausente em endpoints legacy que ainda não passaram pelo resolver. */
    posterSource: PosterSourceSchema.optional(),
});
// ===== Series =====
export const SeriesSchema = z.object({
    id: z.string(),
    name: z.string(),
    poster: z.string().nullable(),
    backdrop: z.string().nullable().optional(),
    plot: z.string().optional(),
    year: z.number().optional(),
    genre: z.string().optional(),
    rating: z.number().optional(),
    ageRating: z.string().optional(),
    cast: z.array(z.string()).optional(),
    castDetailed: z.array(CastMemberSchema).optional(),
    director: z.string().optional(),
    categoryId: z.string(),
    seasonsCount: z.number().default(0),
    lastModifiedAt: z.number().optional(),
    tmdbId: z.number().optional(),
    trailerYoutubeKey: z.string().optional(),
    recommendationsTmdbIds: z.array(z.number()).optional(),
    quality: ContentQualitySchema.optional(),
    source: ContentSourceSchema.optional(),
    audioLanguage: AudioLanguageSchema.optional(),
    hdr: z.boolean().optional(),
    originPlatform: OriginPlatformSchema.optional(),
    /** Idioma original (ISO 639-1). Pra detectar séries brasileiras. */
    originalLanguage: z.string().optional(),
    /** País de origem (ISO 3166-1 alpha-2). TV usa origin_country, não
     *  production_countries (TMDB separa). 'BR' = série nacional. */
    originCountry: z.array(z.string()).optional(),
    /** Score de popularidade do TMDB. Usado em rails "Em alta". */
    popularity: z.number().optional(),
    /** Data primeira exibição (ISO YYYY-MM-DD). Pra rail "Estreias" rolante. */
    firstAirDate: z.string().optional(),
    /** Tokens pré-computados para busca client-side (Opção D do Catálogo
     *  v1, 2026-05-11). Ver VodItemSchema.searchTokens. */
    searchTokens: z.string().optional(),
    /** Ver VodItemSchema.posterSource. */
    posterSource: PosterSourceSchema.optional(),
});
// ===== Disponibilidade no catálogo atual do parceiro =====
/**
 * Resposta do backend pra "este id ainda existe no catálogo do parceiro?".
 *
 * Existe porque o caso contrário já aconteceu em produção: os endpoints
 * enriched-batch devolviam um stub mudo (`{ id, name: '', poster: null }`)
 * pra id que sumiu do catálogo, e o cliente lê isso EXATAMENTE igual a
 * "enriched ainda não chegou" (`enr?.name || p.title` cai no título local
 * nos dois casos). Resultado: card morto no "Continuar assistindo" com
 * aparência saudável, que só falha quando o usuário aperta Assistir.
 *
 * Três estados, e não um booleano, porque "não sei" é um estado REAL e
 * distinto de "não existe": o backend responde a partir do snapshot Redis
 * do parceiro, que pode não estar carregado (cold start / TTL / Redis
 * indisponível). Com booleano, snapshot ausente seria indistinguível de
 * catálogo vazio e o cliente esconderia o rail inteiro numa falha
 * transitória de infra.
 *
 * - `available`  → snapshot carregado e o id ESTÁ nele. Renderiza normal.
 * - `not_found`  → snapshot carregado e o id NÃO está. O parceiro removeu.
 *                  Cliente remove do trilho (sem apagar o progresso local:
 *                  se o parceiro repuser o item, ele volta sozinho).
 * - `unknown`    → snapshot indisponível; o backend não pôde verificar.
 *                  Cliente NÃO esconde nada (fail-safe: preserva o card).
 *
 * Regra de compatibilidade: cliente antigo que ignora o campo se comporta
 * como hoje. Só `not_found` autoriza esconder — qualquer outro valor, ou o
 * campo ausente, significa "mantenha na tela".
 */
export const ContentAvailabilitySchema = z.enum(['available', 'not_found', 'unknown']);
/**
 * Item devolvido pelos endpoints `POST /v1/catalog/{vod,series}/enriched-batch`.
 *
 * `availability` é OBRIGATÓRIO aqui de propósito (e opcional em lugar
 * nenhum): o bug que este tipo fecha foi exatamente um sinal ausente sendo
 * lido como sinal neutro. Campo required = o produtor não consegue esquecer
 * (typecheck reclama) e o consumidor nunca precisa inferir por ausência.
 */
export const EnrichedVodItemSchema = VodItemSchema.extend({
    availability: ContentAvailabilitySchema,
});
export const EnrichedSeriesItemSchema = SeriesSchema.extend({
    availability: ContentAvailabilitySchema,
});
export const VodEnrichedBatchResponseSchema = z.object({
    items: z.array(EnrichedVodItemSchema),
});
export const SeriesEnrichedBatchResponseSchema = z.object({
    items: z.array(EnrichedSeriesItemSchema),
});
// ===== Curated rail item (Fase 1 taxonomia canônica) =====
// Tipo discriminado pra rails que misturam filmes e séries (Em alta,
// Premiados, Drama em alta, etc). Cliente usa `kind` pra escolher
// rota de navegação (/movies/X vs /series/X) e badge visual.
//
// Atributo intersect dos 2 schemas: id, name, poster, rating, year,
// genres-derived. Cada side mantém os próprios extras (vod tem siblings,
// series tem seasonsCount, etc) — simplesmente carrega ambos.
export const CuratedItemSchema = z.discriminatedUnion('kind', [
    VodItemSchema.extend({ kind: z.literal('vod') }),
    SeriesSchema.extend({ kind: z.literal('series') }),
]);
export const CuratedRailSchema = z.object({
    /** Identificador estável do rail (ex: 'trending', 'recently-added',
     *  'genre-drama'). Cliente usa pra cache e telemetria. */
    id: z.string(),
    /** Title key i18n (ex: 'curated.trending') ou string literal. Cliente
     *  resolve via t() se for key, ou exibe direto se literal. */
    title: z.string(),
    items: z.array(CuratedItemSchema),
});
// ===== Stream =====
export const StreamUrlRequestSchema = z.object({
    type: z.enum(['live', 'vod', 'series', 'catchup']),
    streamId: z.string(),
    start: z.number().optional(),
    duration: z.number().optional(),
    ext: z.string().optional(),
    // F3 — failover client-side: endpoint a EVITAR no re-sign. O player passa o
    // `endpointId` que recebeu no sign anterior quando o stream cai na reprodução;
    // o backend pula esse endpoint e resolve o próximo por priority. Posse validada
    // server-side (tem que pertencer ao provider do token); id alheio/inexistente
    // é ignorado (fluxo normal).
    avoidEndpointId: z.string().uuid().optional(),
});
export const SignedStreamUrlSchema = z.object({
    url: z.string().url(),
    expiresAt: z.number(),
    format: z.enum(['hls', 'mpegts', 'mp4', 'dash']),
    // Assinatura HMAC pro proxy /v1/proxy/stream (cliente web). O cliente anexa
    // &exp=<proxyExp>&sig=<proxySig> ao montar a URL do proxy. Opcionais pra
    // retrocompat com respostas antigas (TV nativa nem usa proxy).
    proxySig: z.string().optional(),
    proxyExp: z.number().optional(),
    // F3 — id do endpoint do provider que ESTE sign resolveu. O client guarda e,
    // se o stream cair na reprodução, re-chama o sign com avoidEndpointId = esse id
    // pra forçar o próximo endpoint por priority (failover client-side). Opcional
    // pra retrocompat com respostas/caches antigos sem o campo.
    endpointId: z.string().optional(),
});
// ===== Auth =====
/**
 * `primaryId` de aparelho DECLARADO PELO CLIENTE, no contrato PUBLICADO.
 *
 * As regras vivem em `./device-key` — implementacao UNICA, compartilhada com a borda
 * da API (`apps/api/src/auth/device-bucket-key.ts` importa daqui). A primeira versao
 * desta entrega duplicou o predicado nos dois lados e amarrou os dois por um teste de
 * concordancia; um teste de concordancia so cobre os valores que ele enumera, e duas
 * implementacoes divergem exatamente nos valores que ninguem enumerou.
 *
 * Endurecer o contrato publicado nao e zelo: um contrato que aceita `z.string()` cru
 * enquanto o servidor recusa e uma armadilha esperando o proximo consumidor adota-lo e
 * descobrir o 400 em campo.
 *
 * `max` espelha `devices.primary_id varchar(64)`.
 */
export const ClientDevicePrimaryIdSchema = z
    .string()
    .min(1)
    .max(DEVICE_PRIMARY_ID_MAX)
    .refine((v) => !isReservedDeviceKey(v), { message: DEVICE_KEY_REJECTED_RESERVED })
    .refine((v) => !isMalformedDeviceKey(v), { message: DEVICE_KEY_REJECTED_MALFORMED });
export const DeviceInfoSchema = z.object({
    primaryId: ClientDevicePrimaryIdSchema,
    hardwareHash: z.string().max(64).optional(),
    platform: PlatformSchema,
    platformVersion: z.string().max(40).optional(),
    model: z.string().max(120).optional(),
});
export const LoginRequestSchema = z.object({
    providerCode: z.string().min(4).max(8),
    username: z.string().min(1).max(120),
    password: z.string().min(1).max(120),
    device: DeviceInfoSchema.optional(),
});
export const DirectLoginRequestSchema = z.object({
    listName: z.string().min(1).max(80),
    url: z.string().url(),
    username: z.string().min(1).max(120),
    password: z.string().min(1).max(120),
    device: DeviceInfoSchema.optional(),
});
export const AuthResponseSchema = z.object({
    accessToken: z.string(),
    refreshToken: z.string(),
    expiresIn: z.number(),
    profile: z.object({ username: z.string() }).optional(),
    branding: ProviderBrandingSchema.optional(),
    providerCode: z.string().optional(),
    accountExpiresAt: z.number().nullable().optional(),
});
// ===== Pagination =====
export const PageEnvelopeSchema = (item) => z.object({
    items: z.array(item),
    page: z.number(),
    pageSize: z.number(),
    totalItems: z.number(),
    totalPages: z.number(),
});
//# sourceMappingURL=schemas.js.map